import { useState, useRef, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Link } from "react-router-dom";
import { 
  Sparkles, Eye, Star, Check, Box, Lightbulb, 
  Shield, LayoutGrid, MousePointer2, Home
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useLanguageStore } from "@/stores/languageStore";

// Import assets
import shoeBoxVideo from "@/assets/shoe-display-box-video.mp4";
import shoeBox1 from "@/assets/shoe-display-box-1.jpg";
import shoeBox2 from "@/assets/shoe-display-box-2.jpg";
import shoeBox4 from "@/assets/shoe-display-box-4.jpg";

const features = [
  { icon: MousePointer2, text: "פתיחה בלחיצת כפתור" },
  { icon: Lightbulb, text: "תאורה פנימית יוקרתית" },
  { icon: Shield, text: "הגנה מאבק ולכלוך" },
  { icon: LayoutGrid, text: "ניתן לערימה" },
  { icon: Home, text: "מושלם לבית ולחנות" },
  { icon: Box, text: "פלסטיק קשיח ושקוף" },
];

export const ShoeDisplayShowcase = () => {
  const { t } = useLanguageStore();
  const sectionRef = useRef<HTMLElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const isInView = useInView(sectionRef, { once: true, margin: "-100px" });
  const [activeImage, setActiveImage] = useState(0);
  
  const images = [shoeBox1, shoeBox2, shoeBox4];

  // Auto-rotate images
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveImage((prev) => (prev + 1) % images.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [images.length]);

  return (
    <section 
      ref={sectionRef}
      className="relative py-24 overflow-hidden bg-gradient-to-b from-background via-muted/30 to-background"
    >
      {/* Background Effects */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-1/4 left-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-[150px]" />
        <div className="absolute bottom-0 right-0 w-[500px] h-[500px] bg-accent/5 rounded-full blur-[120px]" />
      </div>

      <div className="container relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.7 }}
          className="text-center mb-16"
        >
          <Badge className="px-5 py-2.5 text-sm bg-gradient-to-r from-primary/20 to-accent/20 text-primary border-primary/30 font-semibold shadow-lg backdrop-blur-sm mb-6">
            <Sparkles className="w-4 h-4 ml-2" />
            חדש בחנות
          </Badge>
          
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            <span className="bg-gradient-to-r from-foreground via-primary to-foreground bg-clip-text text-transparent bg-[length:200%_auto] animate-gradient">
              קופסת תצוגה מוארת לנעליים
            </span>
          </h2>
          
          <p className="text-muted-foreground text-lg md:text-xl max-w-3xl mx-auto leading-relaxed">
            הנעליים שלכם שוות יותר מלהיות זרוקות בבית. הגיע הזמן לאחסן ולהציג כמו שצריך.
          </p>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Video & Images Side */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="relative"
          >
            {/* Main Video */}
            <div className="relative rounded-3xl overflow-hidden shadow-2xl border border-border/50 mb-6">
              <video
                ref={videoRef}
                autoPlay
                loop
                muted
                playsInline
                className="w-full aspect-[4/3] object-cover"
              >
                <source src={shoeBoxVideo} type="video/mp4" />
              </video>
              
              {/* Video Overlay Gradient */}
              <div className="absolute inset-0 bg-gradient-to-t from-background/40 via-transparent to-transparent pointer-events-none" />
              
              {/* Premium Price Badge */}
              <motion.div 
                initial={{ scale: 0, rotate: -5 }}
                animate={isInView ? { scale: 1, rotate: 0 } : {}}
                transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
                className="absolute top-6 right-6"
              >
                <div className="relative">
                  {/* Glow effect */}
                  <div className="absolute inset-0 bg-primary/40 blur-xl rounded-3xl" />
                  
                  {/* Main price card */}
                  <div className="relative bg-gradient-to-br from-card via-card to-muted/80 backdrop-blur-md border border-primary/30 rounded-2xl p-4 shadow-2xl">
                    {/* Sale label */}
                    <div className="absolute -top-3 -right-3 bg-destructive text-destructive-foreground text-xs font-bold px-3 py-1 rounded-full shadow-lg">
                      מבצע
                    </div>
                    
                    {/* Original price */}
                    <div className="text-muted-foreground text-lg line-through decoration-destructive/70 decoration-2 mb-1">
                      ₪420
                    </div>
                    
                    {/* Current price */}
                    <div className="text-3xl font-bold text-primary">
                      ₪380
                    </div>
                    
                    {/* Savings badge */}
                    <div className="text-xs text-muted-foreground mt-1">
                      חוסכים ₪40
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>

            {/* Thumbnail Gallery */}
            <div className="flex gap-3 justify-center">
              {images.map((img, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveImage(idx)}
                  className={`relative w-20 h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                    activeImage === idx 
                      ? "border-primary shadow-lg scale-105" 
                      : "border-border/50 hover:border-primary/50"
                  }`}
                >
                  <img src={img} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
            </div>
          </motion.div>

          {/* Content Side */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="space-y-8"
          >
            {/* Product Title */}
            <div>
              <h3 className="font-display text-2xl md:text-3xl font-bold mb-4">
                דגם FD-102 – צבע לבן
              </h3>
              <p className="text-muted-foreground leading-relaxed">
                קופסה שקופה, קשיחה ומוארת שהופכת כל זוג נעליים לפריט תצוגה יוקרתי — 
                ושומרת עליו נקי ומסודר. בלחיצת כפתור החזית נפתחת בצורה חלקה והנעל "מוגשת" אליכם.
              </p>
            </div>

            {/* Features Grid */}
            <div className="grid grid-cols-2 gap-4">
              {features.map((feature, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.4 + idx * 0.1 }}
                  className="flex items-center gap-3 p-4 rounded-xl bg-card border border-border/50 hover:border-primary/30 transition-all"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                    <feature.icon className="w-5 h-5 text-primary" />
                  </div>
                  <span className="font-medium text-sm">{feature.text}</span>
                </motion.div>
              ))}
            </div>

            {/* Premium Price Display */}
            <div className="bg-gradient-to-r from-card via-muted/30 to-card rounded-2xl p-6 border border-primary/20 shadow-lg">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex items-baseline gap-4">
                  <span className="text-4xl font-bold text-primary">₪380</span>
                  <span className="text-xl text-muted-foreground line-through decoration-destructive/60 decoration-2">₪420</span>
                  <span className="bg-primary/10 text-primary text-sm font-semibold px-3 py-1 rounded-full">
                    חוסכים ₪40
                  </span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star key={star} className="w-4 h-4 fill-primary text-primary" />
                    ))}
                  </div>
                  <span className="text-muted-foreground text-sm">5.0</span>
                </div>
              </div>
              <div className="flex items-center gap-2 mt-3 text-muted-foreground text-sm">
                <Check className="w-4 h-4 text-primary" />
                <span>המחיר הכי טוב בארץ</span>
              </div>
            </div>

            {/* Specs */}
            <div className="bg-muted/50 rounded-2xl p-6 border border-border/50">
              <h4 className="font-semibold mb-4 flex items-center gap-2">
                <Box className="w-5 h-5 text-primary" />
                מפרט טכני
              </h4>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">מידות:</span>
                  <span className="font-medium">37×27.5×27 ס״מ</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">חומר:</span>
                  <span className="font-medium">פלסטיק קשיח</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">צבע:</span>
                  <span className="font-medium">לבן/שקוף</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">תאורה:</span>
                  <span className="font-medium">0.9W מובנית</span>
                </div>
              </div>
            </div>

            {/* CTA Buttons */}
            <div className="flex flex-wrap gap-4">
              <Link to="/product/קופסת-תצוגה-מוארת-לנעליים-fd-102">
                <Button size="lg" className="bg-primary hover:bg-primary/90 text-primary-foreground shadow-xl hover:shadow-2xl transition-all px-8 py-6 text-lg">
                  <Eye className="w-5 h-5 ml-2" />
                  צפה במוצר
                </Button>
              </Link>
              <Link to="/products">
                <Button size="lg" variant="outline" className="border-primary/30 hover:bg-primary/5 px-6 py-6 text-lg">
                  כל המוצרים
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
